@if (sale(); as inv) {
  <div class="flex flex-col gap-4">
    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-info-circle text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Información general</span>
      </div>
      <div class="p-4">
        <div class="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
          <div
            class="shrink-0 flex items-center justify-center [&>i]:text-xs"
            [class]="getStatusIconClasses(inv.status, true)">
            <i [class]="getStatusIcon(inv.status)"></i>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap gap-1">
              <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ getStatusLabel(inv.status) }}</span>
              @if (inv.processStatus) {
                <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ getPipelineStatusLabel(inv.processStatus) }}</span>
              }
              @if (inv.delinquencyStatus && inv.delinquencyStatus !== 'CURRENT') {
                <span class="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{{ getDelinquencyLabel(inv.delinquencyStatus) }}</span>
              }
              @if (inv.saleType) {
                <span class="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{{ getSaleTypeLabel(inv.saleType) }}</span>
              }
            </div>
          </div>
          <span class="text-sm font-bold text-slate-900 shrink-0 tabular-nums">
            {{ (inv.total ?? 0) | currency:(inv.currency || 'MXN'):'symbol':'1.2-2' }}
          </span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-xs font-medium text-slate-500">Fecha de creación</p>
            <p class="text-sm text-slate-700">{{ inv.createdAt ? (inv.createdAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Subtotal</p>
            <p class="text-sm text-slate-700">{{ (inv.subtotal ?? 0) | currency:(inv.currency || 'MXN'):'symbol':'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Impuestos</p>
            <p class="text-sm text-slate-700">{{ (inv.tax ?? 0) | currency:(inv.currency || 'MXN'):'symbol':'1.2-2' }}</p>
          </div>
          @if ((inv.discount ?? 0) > 0) {
            <div>
              <p class="text-xs font-medium text-slate-500">Descuento</p>
              <p class="text-sm text-slate-700">{{ inv.discount | currency:(inv.currency || 'MXN'):'symbol':'1.2-2' }}</p>
            </div>
          }
          <div>
            <p class="text-xs font-medium text-slate-500">Saldo pendiente</p>
            <p class="text-sm font-semibold text-slate-900">{{ (inv.amountDue ?? 0) | currency:(inv.currency || 'MXN'):'symbol':'1.2-2' }}</p>
          </div>
          @if (inv.subscription?.id) {
            <div>
              <p class="text-xs font-medium text-slate-500">Suscripción</p>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 mt-0.5 text-sm text-sky-600 hover:text-sky-800"
                (click)="openSubscriptionDetails()">
                <i class="pi pi-eye text-[10px] shrink-0"></i>
                <span>Ver suscripción</span>
              </button>
            </div>
          }
          <div>
            <p class="text-xs font-medium text-slate-500">Facturación automática</p>
            @if (inv.autoInvoice) {
              <span class="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                <i class="pi pi-check text-[9px]"></i>
                Habilitada
              </span>
            } @else {
              <span class="text-sm text-slate-700">No</span>
            }
          </div>
          @if (inv.paidAt) {
            <div>
              <p class="text-xs font-medium text-slate-500">Fecha de pago</p>
              <p class="text-sm text-slate-700">{{ inv.paidAt | date:'dd/MM/yyyy' }}</p>
            </div>
          }
          @if (inv.notes) {
            <div class="sm:col-span-2">
              <p class="text-xs font-medium text-slate-500">Notas</p>
              <p class="text-sm text-slate-700">{{ inv.notes }}</p>
            </div>
          }
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
          <i class="pi pi-user text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Cliente</span>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-xs font-medium text-slate-500">Nombre comercial</p>
            <p class="text-sm font-medium text-slate-900">{{ inv.customer?.comercialName || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Razón social</p>
            <p class="text-sm text-slate-700">{{ inv.customer?.legalName || '—' }}</p>
          </div>
          @if (inv.customer?.taxId) {
            <div>
              <p class="text-xs font-medium text-slate-500">RFC</p>
              <p class="text-sm text-slate-700 font-mono">{{ inv.customer.taxId }}</p>
            </div>
          }
          @if (inv.customer?.email) {
            <div>
              <p class="text-xs font-medium text-slate-500">Email</p>
              <a
                [href]="'mailto:' + inv.customer.email"
                class="text-sm text-sky-600 hover:text-sky-800">
                {{ inv.customer.email }}
              </a>
            </div>
          }
        </div>
        @if (inv.customer?.id) {
          <button
            type="button"
            class="inline-flex items-center gap-1.5 mt-3 text-sm text-sky-600 hover:text-sky-800"
            (click)="openCustomerDetails()">
            <i class="pi pi-eye text-[10px] shrink-0"></i>
            <span>Ver cliente</span>
          </button>
        }
      </div>
    </div>

    @if (inv.lines && inv.lines.length > 0) {
      <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
            <i class="pi pi-list text-sm"></i>
          </span>
          <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Líneas ({{ inv.lines.length }})</span>
        </div>
        <div class="divide-y divide-slate-100">
          @for (line of inv.lines; track line.id ?? $index) {
            <div class="flex items-center gap-2 px-4 py-2">
              <div class="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-600 [&>i]:text-xs">
                <i class="pi pi-box"></i>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-medium text-slate-900 truncate">{{ line.description || 'Sin descripción' }}</p>
                @if (line.quantity != null && line.unitPrice != null) {
                  <p class="text-[11px] text-slate-500 mt-0.5">
                    {{ line.quantity }} × {{ line.unitPrice | currency:(inv.currency || 'MXN') }}
                  </p>
                }
              </div>
              <span class="text-xs font-bold text-slate-900 shrink-0 tabular-nums">
                {{ (line.totalAmount ?? line.quantity! * (line.unitPrice ?? 0)) | currency:(inv.currency || 'MXN'):'symbol':'1.2-2' }}
              </span>
            </div>
          }
        </div>
      </div>
    }

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
          <i class="pi pi-file-pdf text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Facturas ({{ invoices().length }})</span>
      </div>
      @if (invoicesLoading()) {
        <div class="flex flex-col items-center justify-center py-8 gap-3">
          <i class="pi pi-spin pi-spinner text-2xl text-sky-500"></i>
          <p class="text-sm text-slate-500">Cargando facturas...</p>
        </div>
      } @else if (invoices().length === 0) {
        <p class="text-sm text-slate-500 px-4 py-3">Sin facturas registradas.</p>
      } @else {
        <p-menu #invoiceActionsMenu [model]="invoiceRowMenuItems()" [popup]="true" appendTo="body" />
        <div class="divide-y divide-slate-100">
          @for (invoice of invoices(); track invoice.id ?? $index) {
            <div class="flex items-center gap-2 px-4 py-2">
              <div class="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 [&>i]:text-xs">
                <i class="pi pi-file"></i>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 min-w-0 flex-wrap">
                  <span class="text-xs font-semibold text-slate-900 font-mono truncate">{{ getInvoiceSerieFolio(invoice) }}</span>
                  @if (invoice.issueDate) {
                    <span class="text-[11px] text-slate-500 shrink-0">{{ invoice.issueDate | date:'dd/MM/yyyy' }}</span>
                  }
                  <span [class]="getCfdiStatusBadgeClasses(invoice.status)">
                    {{ getCfdiStatusLabel(invoice.status) }}
                  </span>
                </div>
                @if (invoice.invoiceId) {
                  <p class="text-[11px] text-slate-500 font-mono truncate mt-0.5">{{ invoice.invoiceId }}</p>
                }
              </div>
              <button
                type="button"
                class="shrink-0 flex h-7 w-7 items-center justify-center rounded-md text-sky-600 hover:bg-sky-50 hover:text-sky-800 disabled:opacity-40 disabled:pointer-events-none"
                title="Vista previa PDF"
                [disabled]="!invoice.pdfFileId || isInvoiceActionBusy(invoice)"
                (click)="openCfdiPdfViewer(invoice)">
                <i class="text-xs" [class]="isPreviewLoading(invoice) ? 'pi pi-spin pi-spinner' : 'pi pi-eye'"></i>
              </button>
              <button
                type="button"
                class="shrink-0 flex h-7 w-7 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40 disabled:pointer-events-none"
                title="Acciones"
                [disabled]="(!invoice.pdfFileId && !invoice.xmlFileId) || isInvoiceActionBusy(invoice)"
                (click)="openInvoiceActionsMenu($event, invoice)">
                <i class="text-xs" [class]="isInvoiceMenuLoading(invoice) ? 'pi pi-spin pi-spinner' : 'pi pi-ellipsis-v'"></i>
              </button>
            </div>
          }
        </div>
      }
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
          <i class="pi pi-credit-card text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Cargos relacionados ({{ charges().length }})</span>
      </div>
      @if (charges().length === 0) {
        <p class="text-sm text-slate-500 px-4 py-3">Sin cargos registrados.</p>
      } @else {
        <div class="divide-y divide-slate-100">
          @for (charge of charges(); track charge.id ?? $index) {
            <div class="flex items-center gap-2 px-4 py-2">
              <div
                class="shrink-0 flex items-center justify-center [&>i]:text-xs"
                [class]="getChargeStatusIconClasses(charge.status)">
                <i [class]="getChargeStatusIcon(charge.status)"></i>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5 min-w-0 flex-wrap">
                  @if (charge.paymentNumber != null && charge.numberOfPayments != null) {
                    <span class="text-xs font-medium text-slate-900">
                      Pago {{ charge.paymentNumber }} / {{ charge.numberOfPayments }}
                    </span>
                  }
                  <span [class]="getChargeStatusBadgeClasses(charge.status)">
                    {{ getChargeStatusLabel(charge.status) }}
                  </span>
                </div>
                <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  @if (charge.dueDate) {
                    <span class="text-[11px] text-slate-500">
                      Vence {{ charge.dueDate | date:'dd/MM/yyyy' }}
                    </span>
                  }
                  @if (charge.gracePeriodEndDate) {
                    <span class="text-[11px] text-slate-500">
                      Gracia {{ charge.gracePeriodEndDate | date:'dd/MM/yyyy' }}
                    </span>
                  }
                  @if (charge.paidAt) {
                    <span class="text-[11px] text-slate-500">
                      Pagado {{ charge.paidAt | date:'dd/MM/yyyy' }}
                    </span>
                  }
                  @if (charge.notes) {
                    <span class="text-[11px] text-slate-500 truncate">{{ charge.notes }}</span>
                  }
                </div>
              </div>
              @if (charge.collectionMethod === CollectionMethodEnum.LINK && charge.paymentLink?.link) {
                <button
                  type="button"
                  class="shrink-0 flex h-7 w-7 items-center justify-center rounded-md text-sky-600 hover:bg-sky-50 hover:text-sky-800 disabled:opacity-40 disabled:pointer-events-none"
                  title="Ver recibo público"
                  aria-label="Ver recibo público"
                  [disabled]="isChargeReceiptLoading(charge)"
                  (click)="openChargeReceipt(charge)">
                  <i class="text-xs" [class]="isChargeReceiptLoading(charge) ? 'pi pi-spin pi-spinner' : 'pi pi-external-link'"></i>
                </button>
              }
              <span class="text-xs font-bold text-slate-900 shrink-0 tabular-nums">
                {{ (charge.total ?? 0) | currency:(inv.currency || 'MXN'):'symbol':'1.2-2' }}
              </span>
            </div>
          }
        </div>
      }
    </div>
  </div>
}

<p-dialog
  [visible]="cfdiPdfViewerVisible()"
  (visibleChange)="onCfdiPdfViewerVisibleChange($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="true"
  [dismissableMask]="true"
  [closable]="true"
  appendTo="body"
  [style]="{ width: 'min(56rem, calc(100vw - 1.5rem))' }"
  [contentStyle]="{ padding: '0' }"
  styleClass="cfdi-pdf-viewer-dialog"
  header="CFDI — vista PDF"
  [focusOnShow]="true">
  @if (cfdiPdfViewerSafeUrl(); as pdfSrc) {
    <iframe
      [src]="pdfSrc"
      class="block min-h-[72vh] w-full rounded-b-lg border-0 bg-slate-100"
      title="Vista previa del PDF del CFDI"></iframe>
  } @else {
    <div class="flex min-h-[12rem] items-center justify-center text-slate-500">
      <i class="pi pi-spin pi-spinner mr-2"></i>
      <span>Cargando vista previa…</span>
    </div>
  }
</p-dialog>

<p-dialog
  [visible]="resendCfdiDialogVisible()"
  (visibleChange)="!$event ? closeResendCfdiDialog() : resendCfdiDialogVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [dismissableMask]="!resendCfdiLoading()"
  [closable]="!resendCfdiLoading()"
  appendTo="body"
  [style]="{ width: 'min(26rem, calc(100vw - 2rem))' }"
  header="Reenviar CFDI">
  <div class="flex flex-col gap-4 pt-1">
    <p class="text-sm text-slate-600 m-0">
      Indica el correo al que se enviará el PDF y el XML del CFDI.
    </p>
    <p-floatlabel variant="on">
      <input
        pInputText
        id="sale-read-resend-cfdi-email"
        class="w-full"
        type="email"
        autocomplete="email"
        [ngModel]="resendCfdiEmail()"
        (ngModelChange)="resendCfdiEmail.set($event)"
        [disabled]="resendCfdiLoading()" />
      <label for="sale-read-resend-cfdi-email">Correo electrónico</label>
    </p-floatlabel>
  </div>
  <ng-template #footer>
    <div class="flex justify-end gap-2">
      <p-button
        label="Cancelar"
        severity="secondary"
        [text]="true"
        type="button"
        [disabled]="resendCfdiLoading()"
        (onClick)="closeResendCfdiDialog()" />
      <p-button
        label="Enviar"
        icon="pi pi-send"
        type="button"
        [loading]="resendCfdiLoading()"
        [disabled]="resendCfdiLoading() || !resendCfdiEmail().trim()"
        (onClick)="submitResendCfdi()" />
    </div>
  </ng-template>
</p-dialog>
